import { useCallback, useEffect, useMemo, useState } from "react";
import { Info } from "lucide-react";
import {
  RADIANT_DAY_BLASON_PAGE_COUNT,
  RADIANT_DAY_BLASON_PAGES,
  getGradeLevelForBlasonIndex,
  getRowTitleIdForPageRow,
  getTitleIdForBlasonIndex,
  isBlasonUnlocked,
} from "../constants/radiantDayBlasons";
import { getRadiantDayGradeRequirementTooltip } from "../constants/radiantDayGradeRequirements";
import { fetchRadiantDayTitles } from "../utils/questApi";
import { QUEST_PROGRESS_CHANGED_EVENT } from "../utils/questProgressReporter";
import {
  readRadiantDayBlasonPage,
  writeRadiantDayBlasonPage,
} from "../utils/radiantDayTitlesPreference";
import MenuIconTooltip from "./MenuIconTooltip";
import RadiantBlasonDisplay from "./RadiantBlasonDisplay";
import "./RadiantBlasonDisplay.css";
import "./RadiantDayBlasonsView.css";

const BLASON_ROWS_PER_PAGE = 3;
const BLASON_COLUMNS = 5;

function BlasonCard({ entry, titleProgress }) {
  if (!entry) {
    return <div className="radiant-blasons-view__cell radiant-blasons-view__cell--empty" aria-hidden="true" />;
  }

  const unlocked = isBlasonUnlocked(entry.index, titleProgress);
  const requirementTooltip = getRadiantDayGradeRequirementTooltip(
    getTitleIdForBlasonIndex(entry.index),
    getGradeLevelForBlasonIndex(entry.index)
  );

  return (
    <article className="radiant-blasons-view__cell">
      <RadiantBlasonDisplay
        imageSrc={entry.imageSrc}
        gradeName={entry.gradeName}
        locked={!unlocked}
        requirementTooltip={requirementTooltip}
      />
    </article>
  );
}

function buildRowInfoRows(pageIndex, pageEntries, titleNames) {
  const rows = [];
  for (let rowIndex = 0; rowIndex < BLASON_ROWS_PER_PAGE; rowIndex += 1) {
    const slotIndex = rowIndex * BLASON_COLUMNS + (BLASON_COLUMNS - 1);
    if (!pageEntries[slotIndex]) continue;
    const rowTitleId = getRowTitleIdForPageRow(pageIndex, rowIndex);
    const rowTitleName = rowTitleId ? titleNames[rowTitleId] || "" : "";
    if (!rowTitleName) continue;
    rows.push({ rowIndex, rowTitleName });
  }
  return rows;
}

export default function RadiantDayBlasonsView() {
  const [pageIndex, setPageIndex] = useState(() =>
    readRadiantDayBlasonPage(RADIANT_DAY_BLASON_PAGE_COUNT)
  );
  const [titles, setTitles] = useState([]);
  const [radiantDay, setRadiantDay] = useState(null);

  const loadBlasonProgress = useCallback(async () => {
    try {
      const payload = await fetchRadiantDayTitles();
      setTitles(Array.isArray(payload?.titles) ? payload.titles : []);
      setRadiantDay(
        payload?.radiantDay && typeof payload.radiantDay === "object" ? payload.radiantDay : null
      );
    } catch {
      setTitles([]);
      setRadiantDay(null);
    }
  }, []);

  useEffect(() => {
    loadBlasonProgress();
  }, [loadBlasonProgress]);

  useEffect(() => {
    const handleRefresh = () => {
      loadBlasonProgress();
    };
    window.addEventListener(QUEST_PROGRESS_CHANGED_EVENT, handleRefresh);
    return () => window.removeEventListener(QUEST_PROGRESS_CHANGED_EVENT, handleRefresh);
  }, [loadBlasonProgress]);

  const titleProgress = useMemo(() => {
    const map = {};
    for (const title of titles) {
      map[title.id] = title.achievedGrade;
    }
    if (radiantDay) {
      map[radiantDay.id] = radiantDay.achievedGrade;
    }
    return map;
  }, [titles, radiantDay]);

  const titleNames = useMemo(() => {
    const map = {};
    for (const title of titles) {
      map[title.id] = title.titleName;
    }
    if (radiantDay) {
      map[radiantDay.id] = radiantDay.titleName;
    }
    return map;
  }, [titles, radiantDay]);

  const pageEntries = RADIANT_DAY_BLASON_PAGES[pageIndex] || [];
  const rowInfoRows = useMemo(
    () => buildRowInfoRows(pageIndex, pageEntries, titleNames),
    [pageIndex, pageEntries, titleNames]
  );

  const handlePageChange = (nextPageIndex) => {
    setPageIndex(nextPageIndex);
    writeRadiantDayBlasonPage(nextPageIndex);
  };

  return (
    <div className="radiant-blasons-view">
      <header className="radiant-blasons-view__header">
        <h2 className="radiant-blasons-view__title">Armorial de Vocabularium</h2>
      </header>

      <div className="radiant-blasons-view__body">
        <div className="radiant-blasons-view__stage">
          <div className="radiant-blasons-view__grid">
            {Array.from({ length: 15 }, (_, slotIndex) => (
              <BlasonCard
                key={`${pageIndex}-${slotIndex}`}
                entry={pageEntries[slotIndex] || null}
                titleProgress={titleProgress}
              />
            ))}
          </div>

          <div className="radiant-blasons-view__row-info-layer">
            {rowInfoRows.map(({ rowIndex, rowTitleName }) => (
              <div
                key={rowIndex}
                className="radiant-blasons-view__row-info-anchor"
                style={{ "--row-index": rowIndex }}
              >
                <MenuIconTooltip tooltip={rowTitleName} placement="left" portal>
                  <button
                    type="button"
                    className="vocab-info-button"
                    aria-label={`Titre : ${rowTitleName}`}
                  >
                    <Info size={13} strokeWidth={2} aria-hidden="true" />
                  </button>
                </MenuIconTooltip>
              </div>
            ))}
          </div>

          <div
            className="radiant-blasons-view__nav"
            role="radiogroup"
            aria-label="Pages de blasons"
          >
            {Array.from({ length: RADIANT_DAY_BLASON_PAGE_COUNT }, (_, index) => (
              <label key={index} className="radiant-blasons-view__nav-radio">
                <input
                  type="radio"
                  name="radiant-blason-page"
                  className="radiant-blasons-view__nav-radio-input"
                  checked={pageIndex === index}
                  onChange={() => handlePageChange(index)}
                  aria-label={`Page ${index + 1} sur ${RADIANT_DAY_BLASON_PAGE_COUNT}`}
                />
                <span className="radiant-blasons-view__nav-radio-dot" />
              </label>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
